import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { adaptCustomersPhotosToUI } from 'src/adapters/storeToUI/adaptCustomersPhotosToUI';
import type { CustomersPhotosProps } from 'src/components/organisms/CustomersPhotos/components/CustomersPhotos';
import { CUSTOMERS_PHOTOS_COUNT } from 'src/components/organisms/CustomersPhotos/constants/customersPhotosCount';
import {
  trackCustomerPhotoClick,
  trackCustomersPhotosImpression,
  trackCustomersPhotosNavigate,
} from 'src/components/organisms/CustomersPhotos/tracking/customersPhotosEvents';
import { getReviewsDrawerHref } from 'src/fragmentIDs';
import { selectPhotoReviews } from 'src/redux/reviews/selectors';

/**
 * View model for the customers' photos strip: reads the product's reviews with photos,
 * flattens their photos and wires the tracking. Null until that page lands, when it
 * failed, and whenever it holds no photos, so the layout omits the strip rather than
 * rendering an empty one. There is no loading state to show: photos are rare enough that
 * reserving a row for them would leave most pages with a gap.
 */
export const useCustomersPhotosViewModel = (): CustomersPhotosProps | null => {
  const photoReviews = useSelector(selectPhotoReviews);

  const photos = useMemo(
    () => adaptCustomersPhotosToUI(photoReviews ?? []).slice(0, CUSTOMERS_PHOTOS_COUNT),
    [photoReviews],
  );

  if (photos.length === 0) {
    return null;
  }

  return {
    hrefForReview: getReviewsDrawerHref,
    onNavigate: trackCustomersPhotosNavigate,
    onPhotoClick: (_e, _photo, index) => trackCustomerPhotoClick(index),
    onVisible: trackCustomersPhotosImpression,
    photos,
  };
};
